/**
 * WP-06C-B-V — the governed reason prompt for waiting and blocked.
 *
 * The backend refuses a transition into `waiting` or `blocked` without a reason,
 * so the product must collect one BEFORE it issues the command. This dialog is
 * the only place that reason is captured. It carries no authority of its own: it
 * neither changes a status nor predicts one, it simply returns the sentence the
 * caller passes to the lifecycle command.
 */

import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

/** The same bound the backend enforces, so the refusal happens here instead. */
export const REASON_MAX_LENGTH = 500;

export function StatusReasonDialog({
  open,
  statusLabel,
  busy = false,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  statusLabel: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const trimmed = reason.trim();
  const tooLong = trimmed.length > REASON_MAX_LENGTH;
  const ready = trimmed.length > 0 && !tooLong;

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onCancel())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Why is this {statusLabel.toLowerCase()}?</DialogTitle>
          <DialogDescription>
            A short reason is recorded with the change so the history explains itself later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label
            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            htmlFor="status-reason"
          >
            Reason
          </label>
          <Textarea
            id="status-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="What is this waiting on?"
            rows={3}
            autoFocus
          />
          {tooLong ? (
            <p className="text-xs text-destructive">
              That reason is longer than this change accepts. Shorten it and try again.
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(trimmed)} disabled={!ready || busy}>
            Save status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
